import './App.css';
import './craft.css';
import './atelier.css';
import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Home from './pages/Home';
import { getScrollBehavior } from './lib/motion';

// ── Route-level code-splitting (performance pass 2026-09-01) ──────────────
// "/" (Home) stays eager in the main bundle; every other route is a lazy
// chunk. Header / NavDrawer / StickyBar / constants stay in the main bundle
// (they're imported by Home). Chunks prefetch on link hover/touchstart and
// during idle, so navigation still feels instant.
const routeLoaders = {
  '/carpenter-hobart': () => import('./pages/CarpenterHobart'),
  '/handyman-hobart': () => import('./pages/HandymanHobart'),
  '/decks-and-fences-hobart': () => import('./pages/DecksFencesHobart'),
  '/doors-hobart': () => import('./pages/DoorsHobart'),
  '/framing-carpenter-hobart': () => import('./pages/FramingCarpenterHobart'),
  '/carpenter-bruny-island': () => import('./pages/CarpenterBrunyIsland'),
  '/renovations-hobart': () => import('./pages/RenovationsHobart'),
  '/kitchens-hobart': () => import('./pages/KitchensHobart'),
  '/custom-interiors-hobart': () => import('./pages/CustomInteriorsHobart'),
  '/formwork-hobart': () => import('./pages/FormworkHobart'),
  '/about': () => import('./pages/About'),
};

const CarpenterHobart = lazy(routeLoaders['/carpenter-hobart']);
const HandymanHobart = lazy(routeLoaders['/handyman-hobart']);
const DecksFencesHobart = lazy(routeLoaders['/decks-and-fences-hobart']);
const DoorsHobart = lazy(routeLoaders['/doors-hobart']);
const FramingCarpenterHobart = lazy(routeLoaders['/framing-carpenter-hobart']);
const CarpenterBrunyIsland = lazy(routeLoaders['/carpenter-bruny-island']);
const RenovationsHobart = lazy(routeLoaders['/renovations-hobart']);
const KitchensHobart = lazy(routeLoaders['/kitchens-hobart']);
const CustomInteriorsHobart = lazy(routeLoaders['/custom-interiors-hobart']);
const FormworkHobart = lazy(routeLoaders['/formwork-hobart']);
const About = lazy(routeLoaders['/about']);
const NotFound = lazy(() => import('./pages/NotFound'));
// Online invoice payments (Stripe Checkout) — owner-authorised 2026-09-18.
// Utility routes: noindex, not in the sitemap, not in routeLoaders.
const PayInvoice = lazy(() => import('./pages/PayInvoice'));
const PaySuccess = lazy(() => import('./pages/PaySuccess'));

// Minimal on-brand skeleton while a route chunk loads — header-height black
// bar over a black content area. No spinner, no layout shift.
const RouteFallback = () => (
  <div className="route-fallback" aria-hidden="true">
    <div className="route-fallback-bar" />
  </div>
);

// ── B01D: URL hygiene inside the SPA (NOT HTTP redirects — the hosting
// layer serves 200 for everything; true 301/308 remain unresolved) ────────
// Direct loads are pre-normalised by an inline script in public/index.html
// that runs BEFORE the analytics bootstraps, so the initial page_view
// already carries the clean URL. This component is the safety net for
// client-side navigations to a trailing-slash variant of the 11 known
// non-home routes (routeLoaders keys): replace navigation — no history
// entry, no loop (the target fails the trailing-slash test). The raw
// location.search / location.hash strings are passed through verbatim —
// duplicate, blank and percent-encoded query values survive byte-for-byte
// (URLSearchParams would re-serialise and corrupt them). APIs, assets and
// unknown paths are never touched: unknowns fall through to the branded
// 404, not Home.
const KNOWN_SLASHLESS_ROUTES = new Set(Object.keys(routeLoaders));

const RouteNormalizer = () => {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (pathname.length > 1 && pathname.endsWith('/')) {
      const trimmed = pathname.replace(/\/+$/, '');
      if (KNOWN_SLASHLESS_ROUTES.has(trimmed)) {
        navigate({ pathname: trimmed, search, hash }, { replace: true });
      }
    }
  }, [pathname, search, hash, navigate]);
  return null;
};

// The browser's native #anchor jump runs before the SPA has rendered the
// target element, so a direct/redirected URL carrying a hash would land at
// the top. This helper is rendered INSIDE the same Suspense boundary as
// Routes (see below): on a cold lazy chunk it suspends with the route and
// MOUNTS on the same commit as the route content — anchor arrival is tied
// to the content commit itself, not a timer, so even a >6 s chunk load
// still arrives. The MutationObserver + 6 s bound remain only as a
// backstop for post-commit async renders; cleanup cancels on any
// route/hash change so a stale observer can never scroll a later page.
// ServicePage's scroll-to-top effect skips when a hash is present — the
// two never fight. (B01D-NAV-01: a one-shot 250 ms check lost the anchor
// permanently on slow chunk loads.)
const ScrollToHash = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    let id;
    try { id = decodeURIComponent(hash.slice(1)); } catch (_) { return; }
    if (!id) return;
    let done = false;
    const giveUp = setTimeout(() => { done = true; observer.disconnect(); }, 6000);
    const tryScroll = () => {
      if (done) return;
      const el = document.getElementById(id);
      if (!el) return;
      done = true;
      observer.disconnect();
      clearTimeout(giveUp);
      el.scrollIntoView({ behavior: getScrollBehavior(), block: 'start' });
    };
    const observer = new MutationObserver(tryScroll);
    observer.observe(document.getElementById('root') || document.body, { childList: true, subtree: true });
    tryScroll(); // target may already be mounted (warm/cached chunk)
    return () => { done = true; observer.disconnect(); clearTimeout(giveUp); };
  }, [pathname, hash]);
  return null;
};

// Legacy Google-ad URL (/carpenter/hobart, trailing slash tolerated by the
// router's matching). Keeps the click alive AND preserves the raw query
// string + hash — the ad's gclid/utm attribution must survive.
const LegacyCarpenterRedirect = () => {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: '/carpenter-hobart', search, hash }} replace />;
};

// Router map:
//   /                           → Home (single-page marketing site, eager)
//   /carpenter-hobart           → Qualified carpenter service page
//   /handyman-hobart            → Handyman service page
//   /decks-and-fences-hobart    → Decks & fences service page
//   /doors-hobart               → Doors service page
//   /framing-carpenter-hobart   → Subcontract framing service page
//
// Legacy redirect:
//   /carpenter/hobart           → 301-style redirect to /carpenter-hobart.
//     A live Google ad points at the old URL; this keeps that click alive
//     while the ad landing page is switched over. Do NOT delete until the
//     ad has been retargeted for at least two weeks.
//
// Catch-all:
//   *                           → Branded 404 page.
function App() {
  // Prefetch route chunks on link hover / touchstart, and all of them during
  // idle — navigation feels instant even though every route is split out.
  useEffect(() => {
    const prefetchFromEvent = (e) => {
      const a = e.target && e.target.closest ? e.target.closest('a[href^="/"]') : null;
      if (!a) return;
      try {
        const path = new URL(a.href).pathname;
        if (routeLoaders[path]) routeLoaders[path]();
      } catch (_) { /* ignore malformed hrefs */ }
    };
    document.addEventListener('pointerover', prefetchFromEvent, { passive: true });
    document.addEventListener('touchstart', prefetchFromEvent, { passive: true });
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 2000));
    idle(() => Object.values(routeLoaders).forEach((load) => load()));
    return () => {
      document.removeEventListener('pointerover', prefetchFromEvent);
      document.removeEventListener('touchstart', prefetchFromEvent);
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <RouteNormalizer />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/carpenter-hobart" element={<CarpenterHobart />} />
            <Route path="/handyman-hobart" element={<HandymanHobart />} />
            <Route path="/decks-and-fences-hobart" element={<DecksFencesHobart />} />
            <Route path="/doors-hobart" element={<DoorsHobart />} />
            <Route path="/framing-carpenter-hobart" element={<FramingCarpenterHobart />} />
            <Route path="/carpenter-bruny-island" element={<CarpenterBrunyIsland />} />
            <Route path="/renovations-hobart" element={<RenovationsHobart />} />
            <Route path="/kitchens-hobart" element={<KitchensHobart />} />
            <Route path="/custom-interiors-hobart" element={<CustomInteriorsHobart />} />
            <Route path="/formwork-hobart" element={<FormworkHobart />} />
            <Route path="/about" element={<About />} />
            <Route path="/pay" element={<PayInvoice />} />
            <Route path="/pay/success" element={<PaySuccess />} />

            {/* Legacy Google-ad URL. Retargeting pending — see comment above.
                Query string + hash (gclid/utm attribution, #anchors) survive. */}
            <Route path="/carpenter/hobart" element={<LegacyCarpenterRedirect />} />

            {/* Wildcard — branded 404, never a blank page. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          {/* Inside the Suspense boundary on purpose: mounts on the same
              commit as the route content — see ScrollToHash comment. */}
          <ScrollToHash />
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
